import React from "react";
import useIsMobile from "@/hooks/useIsMobile";
import useSwipeAction from "@/hooks/useSwipeAction";
import { cn } from "@/lib/utils";

/**
 * SwipeRow — Instagram/iOS-style swipeable list row.
 * Desktop: renders children inline (no-op wrapper).
 * Mobile: wraps with useSwipeAction. Swipe-right = primary action, swipe-left = secondary.
 *
 * Colored hint reveal during swipe is handled by useSwipeAction internally (green for
 * right/primary, red for left/destructive).
 *
 * Usage:
 *   <SwipeRow
 *     onSwipeRight={() => markComplete(task.id)}
 *     onSwipeLeft={() => snooze(task.id)}
 *     rightHint="✓ Complete"
 *     leftHint="💤 Snooze"
 *   >
 *     <TaskCard task={task} />
 *   </SwipeRow>
 */
export function SwipeRow({
  onSwipeRight,
  onSwipeLeft,
  rightHint,
  leftHint,
  children,
  className,
  disabled = false,
  threshold = 80,
}) {
  const isMobile = useIsMobile();
  const swipe = useSwipeAction({
    onSwipeRight: disabled ? null : onSwipeRight,
    onSwipeLeft: disabled ? null : onSwipeLeft,
    threshold,
  });

  if (!isMobile || disabled || (!onSwipeRight && !onSwipeLeft)) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Underlay hints (revealed during swipe via useSwipeAction's background colour) */}
      {(rightHint || leftHint) && (
        <div className="absolute inset-0 flex justify-between items-center pointer-events-none px-4 text-sm font-medium">
          {onSwipeRight && (
            <span className="text-green-400 opacity-0" data-swipe-hint="right">{rightHint}</span>
          )}
          <span />
          {onSwipeLeft && (
            <span className="text-red-400 opacity-0" data-swipe-hint="left">{leftHint}</span>
          )}
        </div>
      )}
      <div {...swipe.handlers}>{children}</div>
    </div>
  );
}

export default SwipeRow;
